import React from 'react';
import { Button } from '../ui/button';
import { FileImage, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../../utils/imageCompression';
import { ImageWithFallback } from '../ui/image-with-fallback';

/**
 * Compact business-permit / DTI / SEC uploader. Used inside the pending or
 * rejected resubmit view so merchants can replace their permit document
 * without leaving the page.
 */
export function BusinessPermitCard({ api, currentUrl, onUpdated, businessName = '' }) {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const before = f.size;
      const compressed = await compressImage(f, { maxSide: 1600, quality: 0.85 });
      if (compressed.size < before) {
        toast.message(`Permit compressed: ${formatCompressionStats(before, compressed.size)}`);
      }
      const fd = new FormData();
      fd.append('file', compressed);
      fd.append('purpose', 'business-permit');
      if (businessName) fd.append('subject_name', businessName);
      const up = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = up.data.url || up.data.file_url;
      await api.put('/api/merchant/profile', { business_permit_url: url });
      toast.success('Business permit updated');
      onUpdated?.(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload permit');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#000B18]/40 p-4" data-testid="business-permit-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-20 h-20 rounded-lg bg-[#000B18] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl
            ? <ImageWithFallback
                src={currentUrl}
                alt="Business permit"
                className="w-full h-full object-cover"
                fallback={<FileImage className="w-8 h-8 text-[#5C7599]" data-testid="business-permit-fallback" />}
              />
            : <FileImage className="w-8 h-8 text-[#5C7599]" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-outfit font-semibold text-sm mb-1">Business Permit / DTI</h4>
          <p className="text-[#A0B4CC] text-xs mb-2">
            Upload a clear photo or scan of your latest business permit, DTI, or SEC registration.
          </p>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="amend-permit-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm"
            className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
            data-testid="amend-permit-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (currentUrl ? 'Replace permit' : 'Upload permit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
